import { Component, OnInit } from '@angular/core';
import { MultiChatService } from '../../../shared/services/multi-chat.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { combineLatest, merge, Observable, Subject } from 'rxjs';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ClickhouseService } from '../../../shared/services/clickhouse.service';
import { SocketService } from '../../../shared/services/socket.service';
import { getBillingInfo } from '../../../store/actions/billing.actions';
import { Store } from '@ngrx/store';
import { AppStateInterface } from '../../../store/state/app.state';
import { isUserMultiTaskSelector } from '../../../store/selectors/profile.selectors';
import { setMultiTaskStatus } from '../../../store/actions/profile.actions';
import { TicketInfoInterface } from './models/ticket-info.interface';
import { debounceTime, delay, map, tap } from 'rxjs/operators';

@Component({
  selector: 'app-multi-chat',
  templateUrl: './multi-chat.component.html',
  styleUrls: ['./multi-chat.component.scss'],
})
export class MultiChatComponent implements OnInit {
  updatedTickets: Set<number> = new Set<number>();

  tickets: Array<TicketInfoInterface> = [];

  chosenTicket: TicketInfoInterface | undefined = undefined;

  currentTicketEvent = {
    onExpertTyping$: new Subject<boolean>(),
    onNewMessage$: new Subject<boolean>(),
    onTicketUnsolvedApprove$: new Subject<{ ticket_id: number }>(),
    onAskQuestion$: new Subject<{ ticket_id: number }>(),
    onMessagesRead$: new Subject<{
      messages: Array<{ author: string; messageId: number; status: string }>;
    }>(),
    onSetRate$: new Subject<{ ticket_id: number }>(),
  };

  currentTicketEventFlag = {
    isExpertTyping: false,
    ticketHasNewMessage: false,
  };

  showMoreBtnShow: boolean = false;

  constructor(
    private multiChatService: MultiChatService,
    private spinner: NgxSpinnerService,
    private modalService: NgbModal,
    private clickHouse: ClickhouseService,
    private socketService: SocketService,
    private store: Store<AppStateInterface>,
  ) {
    combineLatest([this.store.select(isUserMultiTaskSelector)]).subscribe(([isUserMultitask]) => {
      if (!isUserMultitask) {
        this.store.dispatch(setMultiTaskStatus());
      }
    });
    this.multiChatService.userOutFromTicket$.subscribe((action) => {
      if (action) {
        this.chosenTicket = undefined;
      }
    });
  }

  ngOnInit(): void {
    this.spinner.show();
    this.subscribeChatEvents();
    this.multiChatService.getTickets().subscribe((ticketsResponse) => {
      this.tickets = ticketsResponse.data.tickets;
      this.showMoreBtnShow = ticketsResponse.data.show_more;
      this.spinner.hide();
    });
  }

  onShowMoreTickets() {
    this.showMoreBtnShow = false;
    this.spinner.show('loadMoreTicketsSpinner');
    this.multiChatService
      .getTickets(this.tickets[this.tickets.length - 1].id)
      .pipe(delay(1000))
      .subscribe((ticketsResponse) => {
        ticketsResponse.data.tickets
          .sort((a: any, b: any) => b.id - a.id)
          .forEach((ticket: any) => {
            if (!this.tickets.find((t) => ticket.id === t.id)) {
              this.tickets.push(ticket);
            }
          });
        this.showMoreBtnShow = ticketsResponse.data.show_more;
        this.spinner.hide('loadMoreTicketsSpinner');
      });
  }

  onChooseTicket(event: TicketInfoInterface) {
    this.chosenTicket = event;
  }

  createNewTask() {
    import('../chat-components/modals/attach-modal/attach-modal-component.module').then(
      ({ AttachModalComponentModule }) => {
        const AttachModalComponent = AttachModalComponentModule.getComponent();
        const modal = this.modalService.open(AttachModalComponent, {
          backdrop: 'static',
          keyboard: false,
        });
        modal.componentInstance.dataAttached.subscribe((data: any) => {
          this.choseAnswerType(data);
        });
      },
    );
    this.clickHouse.storeEvent('create_new_task_btn_click').subscribe(() => {});
  }

  private choseAnswerType(data: any) {
    import('../chat-components/modals/answer-type-modal/answer-type-modal-component.module').then(
      ({ AnswerTypeModalComponentModule }) => {
        const AnswerTypeModalComponent = AnswerTypeModalComponentModule.getComponent();
        const modal = this.modalService.open(AnswerTypeModalComponent, {
          backdrop: 'static',
          keyboard: false,
          centered: true,
        });
        modal.componentInstance.data = data;
        modal.componentInstance.answerTypeSet.subscribe((i: any) => {
          this.attachData(i).subscribe(() => {});
        });
      },
    );
  }

  private attachData(data: any) {
    return new Observable((subscriber) => {
      if (data.file && data.description) {
        this.multiChatService
          .sendFileMessageToTicket(data.file, '', data.comment_answer)
          .subscribe((respF) => {
            this.tickets.unshift(respF.data.ticket);
            this.chosenTicket = respF.data.ticket;
            this.multiChatService
              .sendTextMessageToTicket(
                data.description,
                '',
                data.comment_answer,
                respF.data.ticket.id,
              )
              .subscribe((respT) => {
                subscriber.next({ respF, respT });
              });
          });
      } else {
        if (data.file) {
          this.multiChatService
            .sendFileMessageToTicket(data.file, '', data.comment_answer)
            .subscribe((resp: any) => {
              this.tickets.unshift(resp.data.ticket);
              this.chosenTicket = resp.data.ticket;
              subscriber.next({});
            });
        } else {
          this.multiChatService
            .sendTextMessageToTicket(data.description, '', data.comment_answer)
            .subscribe((resp: any) => {
              this.tickets.unshift(resp.data.ticket);
              this.chosenTicket = resp.data.ticket;
              subscriber.next({});
            });
        }
      }
    });
  }

  private subscribeChatEvents() {
    this.subscribeTyping();
    this.subscribeNewMessage();
    merge(
      this.currentTicketEvent.onTicketUnsolvedApprove$,
      this.currentTicketEvent.onAskQuestion$,
      this.currentTicketEvent.onSetRate$,
      this.socketService.on('solution_event').pipe(
        tap(() => {
          this.showMathMasterInstallModal();
        }),
      ),
      this.socketService.on('ticket_close'),
      this.socketService.on('unlocked'),
      this.socketService.on('ticket_unlocked'),
      this.socketService.on('ticket_opened_for_lock'),
      this.socketService.on('ticket_waiting_for_solve'),
      this.socketService.on('ticket_solved'),
      this.socketService.on('ticket_close_unsolved'),
      this.socketService.on('chat_message').pipe(
        map((resp) => {
          const ticketIndex = this.tickets.findIndex((ticket) => ticket.id == resp.ticket_id);
          if (this.chosenTicket?.id == resp.ticket_id) {
            this.currentTicketEvent.onNewMessage$.next(true);
          }
          return ticketIndex < 0 ? resp : { ticket_id: 0 };
        }),
      ),
      merge(
        this.socketService.on('billing_balance').pipe(
          tap(() => {
            this.store.dispatch(getBillingInfo());
          }),
        ),
        this.socketService.on('expert_typing').pipe(
          tap((resp) => {
            if (this.chosenTicket?.id == resp.ticket_id) {
              this.currentTicketEvent.onExpertTyping$.next(true);
            }
          }),
        ),
        this.socketService.on('comment_event').pipe(
          tap((resp) => {
            this.currentTicketEvent.onMessagesRead$.next(resp);
          }),
        ),
      ).pipe(
        map(() => {
          return { ticket_id: 0 };
        }),
      ),
    )
      .pipe(
        tap((resp) => {
          if (resp.ticket_id) {
            this.updatedTickets.add(+resp.ticket_id);
          }
        }),
        debounceTime(1000),
      )
      .subscribe(() => {
        if (Array.from(this.updatedTickets.values()).length) {
          this.updateTickets(Array.from(this.updatedTickets.values()));
          this.updatedTickets.clear();
        }
      });
  }

  private subscribeNewMessage() {
    this.currentTicketEvent.onNewMessage$
      .pipe(
        tap(() => {
          this.currentTicketEventFlag.ticketHasNewMessage = true;
        }),
        debounceTime(1),
      )
      .subscribe(() => {
        this.currentTicketEventFlag.ticketHasNewMessage = false;
      });
  }

  private subscribeTyping() {
    this.currentTicketEvent.onExpertTyping$
      .pipe(
        tap(() => {
          this.currentTicketEventFlag.isExpertTyping = true;
        }),
        debounceTime(1000),
      )
      .subscribe(() => {
        this.currentTicketEventFlag.isExpertTyping = false;
      });
  }

  private replaceTicketToFirst(
    id: number | string,
    sourceArray: Array<TicketInfoInterface>,
  ): Array<TicketInfoInterface> {
    const replacedElementIndex = sourceArray.findIndex((el) => el.id == id);
    if (replacedElementIndex && replacedElementIndex !== -1) {
      const srcArray = sourceArray;
      const replacedElement = srcArray.splice(replacedElementIndex, 1)[0];
      srcArray.unshift(replacedElement);
      return srcArray;
    } else {
      return sourceArray;
    }
  }

  private updateTickets(ticketIds: Array<number>) {
    this.multiChatService
      .getTickets(undefined, undefined, ticketIds)
      .subscribe((ticketsResponse) => {
        for (const ticketId of ticketIds) {
          const updTicketIndex = this.tickets.findIndex((ticket) => ticket.id == ticketId);
          const newTicketIndex = ticketsResponse.data.tickets.findIndex(
            (ticket) => ticket.id == ticketId,
          );
          if (updTicketIndex !== -1) {
            this.tickets[updTicketIndex] = ticketsResponse.data.tickets[newTicketIndex];
            this.tickets = this.replaceTicketToFirst(ticketId, this.tickets);
            if (this.chosenTicket?.id == ticketsResponse.data.tickets[newTicketIndex].id) {
              this.chosenTicket = ticketsResponse.data.tickets[0];
            }
          } else {
            this.tickets.unshift(ticketsResponse.data.tickets[newTicketIndex]);
          }
        }
      });
  }

  private showMathMasterInstallModal() {
    const solvedTickets = this.tickets.filter(
      (value) => value.status === 'closed' && value.is_charged_solution,
    );
    const userAgent = navigator.userAgent || navigator.vendor;
    if (/android/i.test(userAgent) || (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream)) {
      if (solvedTickets.length === 1) {
        import('../../../components/other/mathmaster-modal/mathmaster-modal-component.module').then(
          ({ MathmasterModalComponentModule }) => {
            const mathMasterInstallModal = MathmasterModalComponentModule.getComponent();
            this.modalService.open(mathMasterInstallModal, {
              centered: true,
            });
          },
        );
      }
    }
  }
}
